import {
  AttributeType,
  DuctHeatLossGainInputType,
  ExistingSystemComponentType,
  ExistingSystemType,
  LevelType
} from 'data/types';
import { levelHasFullDuctwork, levelHasPartialDuctwork } from 'utils/ductwork';
import { isExistingEquipmentComponentPlaced } from 'utils/existing-equipment';
import { isLevelConditioned } from 'utils/level-conditioning';

// Attribute-answer strings (SystemSelector.AttributeValues). NOTE: the
// crawl/basement answer has a SINGLE space before 'System' — the ManualJ
// Table7Description row (and DuctHeatLossGainInputType) carries a DOUBLE
// space; toCanonicalDuctSystemType below bridges that at the API boundary.
export const DUCT_SYSTEM_TYPE_ANSWERS = {
  attic: 'Radial or Spider Supply and Return System in 16B Attic',
  crawlOrUnconditionedBasement:
    'Trunk and Branch Supply and Return System in Closed Crawlspace or Unconditioned Basement',
  conditionedSpace: 'Ducts in Conditioned Space'
} as const;

export type DerivedDuctDefaults = {
  ductSystemType: string;
  ductWallInsulation: string;
  ductLeakageClass: string;
};

// Indoor ducted units only: their placement is evidence of where the duct
// system lives. Outdoor units (condensers/heat pumps), ductless air handlers,
// PTACs and unknown components say nothing about duct location.
const INDOOR_DUCTED_COMPONENT_TYPES = new Set<
  ExistingSystemComponentType['type']
>([
  'Split System Furnace',
  'Split System Air Handler',
  'Split System Evaporator Coil',
  'Split System Modular Blower',
  'Self Contained Electric AC',
  'Self Contained Electric Heat Pump',
  'Self Contained Gas Pack With AC',
  'Self Contained Gas Pack With Heat Pump',
  'Next-Gen Evaporator Coil',
  'Next-Gen Air Handler'
]);

// level_ductwork means "this level is served", not "the trunk runs here", so
// any plausible unconditioned duct-run space (attic/crawl/unconditioned
// basement) anywhere in the structure forbids the zero-loss conditioned-space
// conclusion.
function structureHasUnconditionedDuctSpace(structure: LevelType[]): boolean {
  return structure.some(
    (level) =>
      (level.type === 'attic' ||
        level.type === 'crawlspace' ||
        level.type === 'basement') &&
      !isLevelConditioned(level)
  );
}

function deriveDuctSystemType(
  structure: LevelType[],
  existingSystems: ExistingSystemType[]
): string {
  const levelsByUniqueId = new Map(
    structure.map((level) => [level.uniqueId, level])
  );
  const hasUnconditionedDuctSpace =
    structureHasUnconditionedDuctSpace(structure);

  // Tier 1: placed indoor equipment, worst location wins.
  const placedIndoorLevels = existingSystems
    .flatMap((system) => system.components)
    .filter(
      (component) =>
        INDOOR_DUCTED_COMPONENT_TYPES.has(component.type) &&
        isExistingEquipmentComponentPlaced(component)
    )
    .map((component) => levelsByUniqueId.get(component.levelUniqueId!))
    .filter((level): level is LevelType => !!level);

  if (placedIndoorLevels.length > 0) {
    if (placedIndoorLevels.some((level) => level.type === 'attic')) {
      return DUCT_SYSTEM_TYPE_ANSWERS.attic;
    }
    if (placedIndoorLevels.some((level) => !isLevelConditioned(level))) {
      return DUCT_SYSTEM_TYPE_ANSWERS.crawlOrUnconditionedBasement;
    }
    return hasUnconditionedDuctSpace
      ? DUCT_SYSTEM_TYPE_ANSWERS.crawlOrUnconditionedBasement
      : DUCT_SYSTEM_TYPE_ANSWERS.conditionedSpace;
  }

  // Tier 2: per-level ductwork answers.
  const ductworkLevels = structure.filter(
    (level) => levelHasFullDuctwork(level) || levelHasPartialDuctwork(level)
  );

  if (ductworkLevels.length > 0) {
    if (ductworkLevels.some((level) => level.type === 'attic')) {
      return DUCT_SYSTEM_TYPE_ANSWERS.attic;
    }
    if (
      ductworkLevels.some(
        (level) =>
          level.type === 'crawlspace' ||
          (level.type === 'basement' && !isLevelConditioned(level))
      )
    ) {
      return DUCT_SYSTEM_TYPE_ANSWERS.crawlOrUnconditionedBasement;
    }
    if (ductworkLevels.every((level) => isLevelConditioned(level))) {
      return hasUnconditionedDuctSpace
        ? DUCT_SYSTEM_TYPE_ANSWERS.crawlOrUnconditionedBasement
        : DUCT_SYSTEM_TYPE_ANSWERS.conditionedSpace;
    }
  }

  // Tier 3: underivable — mid-severity realistic default (attic would be
  // worst-case, conditioned space best-case).
  return DUCT_SYSTEM_TYPE_ANSWERS.crawlOrUnconditionedBasement;
}

// Realistic (not best-case) duct defaults for the DUCTED pathway, derived
// from equipment placement / per-level ductwork answers where possible.
// Ductless keeps data/sizing-calculator-defaults' defaultDucts (zero-loss
// 'Ducts in Conditioned Space') — callers gate on the pathway.
export function deriveDefaultDucts(
  structure: LevelType[],
  existingSystems: ExistingSystemType[],
  projectSettings: AttributeType[]
): DerivedDuctDefaults {
  const isNewConstruction =
    projectSettings.find((ps) => ps.code === 'is_new_construction')?.value ===
    true;

  return {
    ductSystemType: deriveDuctSystemType(structure, existingSystems),
    ductWallInsulation: 'R-4',
    ductLeakageClass: isNewConstruction ? '0.12 / 0.24' : '0.24 / 0.47'
  };
}

// Blower-heat discount for the sizing request: an explicit user answer wins;
// unset defaults by pathway — ducted systems get no discount (No), ductless
// keep the historical Yes.
export function resolveBlowerHeatDiscount(
  explicit: unknown,
  isDucted: boolean
): boolean {
  if (typeof explicit === 'boolean') return explicit;
  return !isDucted;
}

// Boundary mapping: attribute-answer string -> canonical ManualJ/API string.
// pnet-api matches ductSystemType VERBATIM against
// [sizing].[ManualJ].[Table7Description].description and THROWS on no match,
// and the 7D-AE row has a double space between 'Return' and 'System' while
// the attribute answer has a single one. Apply wherever a sizing request is
// built. Unknown values pass through unchanged (future answers must not
// throw here).
const CANONICAL_DUCT_SYSTEM_TYPE_OVERRIDES: Record<
  string,
  DuctHeatLossGainInputType['ductSystemType']
> = {
  'Trunk and Branch Supply and Return System in Closed Crawlspace or Unconditioned Basement':
    'Trunk and Branch Supply and Return  System in Closed Crawlspace or Unconditioned Basement'
};

export function toCanonicalDuctSystemType(
  value: string
): DuctHeatLossGainInputType['ductSystemType'] {
  return (CANONICAL_DUCT_SYSTEM_TYPE_OVERRIDES[value] ??
    value) as DuctHeatLossGainInputType['ductSystemType'];
}
